import { Moon } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: {
    label: 'Quiet Mode',
    description: 'Remove progress indicators, prompts, and nudges. Keep creation and history accessible.',
    quiet: 'Quiet',
    enableAria: 'Enable Quiet Mode',
    disableAria: 'Disable Quiet Mode',
  },
  es: {
    label: 'Modo Silencioso',
    description: 'Elimina indicadores de progreso, avisos y recordatorios. Mantén la creación y el historial accesibles.',
    quiet: 'Silencio',
    enableAria: 'Activar Modo Silencioso',
    disableAria: 'Desactivar Modo Silencioso',
  },
  fr: {
    label: 'Mode Calme',
    description: 'Supprime les indicateurs de progression, invitations et rappels. Création et historique restent accessibles.',
    quiet: 'Calme',
    enableAria: 'Activer le Mode Calme',
    disableAria: 'Désactiver le Mode Calme',
  },
  pt: {
    label: 'Modo Silencioso',
    description: 'Remove indicadores de progresso, prompts e lembretes. Criação e histórico continuam acessíveis.',
    quiet: 'Silêncio',
    enableAria: 'Ativar Modo Silencioso',
    disableAria: 'Desativar Modo Silencioso',
  },
  ar: {
    label: 'الوضع الهادئ',
    description: 'إزالة مؤشرات التقدم والتنبيهات والتذكيرات. الإنشاء والسجل يبقيان متاحين.',
    quiet: 'هادئ',
    enableAria: 'تفعيل الوضع الهادئ',
    disableAria: 'تعطيل الوضع الهادئ',
  },
  ja: {
    label: 'クワイエットモード',
    description: '進捗インジケーター、プロンプト、ナッジを削除します。作成と履歴はそのまま利用可能です。',
    quiet: '静か',
    enableAria: 'クワイエットモードを有効にする',
    disableAria: 'クワイエットモードを無効にする',
  },
  ko: {
    label: '조용한 모드',
    description: '진행 표시, 프롬프트, 넛지를 제거합니다. 만들기와 기록은 계속 사용할 수 있습니다.',
    quiet: '조용',
    enableAria: '조용한 모드 활성화',
    disableAria: '조용한 모드 비활성화',
  },
  id: {
    label: 'Mode Tenang',
    description: 'Hapus indikator kemajuan, prompt, dan pengingat. Pembuatan dan riwayat tetap dapat diakses.',
    quiet: 'Tenang',
    enableAria: 'Aktifkan Mode Tenang',
    disableAria: 'Nonaktifkan Mode Tenang',
  },

  hi: {
    label: 'शांत मोड',
    description: 'प्रगति संकेतक, प्रॉम्प्ट और नजेस हटाएँ। निर्माण और इतिहास को सुलभ रखें।',
    quiet: 'शांत',
    enableAria: 'शांत मोड सक्षम करें',
    disableAria: 'शांत मोड अक्षम करें',
  },
};

interface QuietModeToggleProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  className?: string;
  variant?: 'compact' | 'full';
}

export function QuietModeToggle({ 
  enabled, 
  onToggle, 
  className,
  variant = 'full' 
}: QuietModeToggleProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);

  if (variant === 'compact') {
    return (
      <button
        onClick={() => onToggle(!enabled)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors',
          enabled 
            ? 'bg-muted text-muted-foreground' 
            : 'bg-transparent text-muted-foreground hover:bg-muted/50',
          className
        )}
        aria-label={enabled ? t.disableAria : t.enableAria}
      >
        <Moon className={cn('h-4 w-4', enabled && 'fill-current')} />
        {enabled && <span className="text-xs">{t.quiet}</span>}
      </button>
    );
  }

  return (
    <div className={cn('flex items-center justify-between rounded-lg border p-4', className)}>
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <Moon className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="quiet-mode" className="font-medium">
            {t.label}
          </Label>
        </div>
        <p className="text-sm text-muted-foreground">
          {t.description}
        </p>
      </div>
      <Switch
        id="quiet-mode"
        checked={enabled}
        onCheckedChange={onToggle}
      />
    </div>
  );
}
